import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchUser, createUserWithAuth, updateUser } from "@/api/users";
import { fetchUserRoles } from "@/api/userRoles";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Save,
  User,
  Shield,
  AlertCircle,
  ShieldAlert,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { z } from "zod";
import { MediaPicker } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Age group options
const AGE_GROUPS = [
  "Under 18",
  "18–24",
  "25–34",
  "35–44",
  "45–54",
  "55–64",
  "65+",
] as const;

// Validation schema for user form
const userSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Invalid email address" }),
  avatarUrl: z.string().optional(),
  ageGroup: z
    .enum(AGE_GROUPS)
    .optional()
    .or(z.literal("none"))
    .or(z.undefined()),
  isActive: z.boolean().optional(),
});

type UserFormData = z.infer<typeof userSchema>;

// Get color for role badge
const getRoleBadgeColor = (roleId: number): string => {
  // Special colors for specific roles
  if (roleId === 1) {
    // SuperAdmin - Red
    return "bg-red-500/10 text-red-500 border-red-500/20";
  }
  if (roleId === 5) {
    // Standard User - Blue
    return "bg-blue-500/10 text-blue-500 border-blue-500/20";
  }

  // Define a set of vibrant transparent background and text color pairs
  const colorPairs = [
    "bg-primary/10 text-primary border-primary/20",
    "bg-blue-500/10 text-blue-500 border-blue-500/20",
    "bg-amber-500/10 text-amber-500 border-amber-500/20",
    "bg-green-500/10 text-green-500 border-green-500/20",
    "bg-purple-500/10 text-purple-500 border-purple-500/20",
    "bg-rose-500/10 text-rose-500 border-rose-500/20",
    "bg-teal-500/10 text-teal-500 border-teal-500/20",
    "bg-orange-500/10 text-orange-500 border-orange-500/20",
    "bg-indigo-500/10 text-indigo-500 border-indigo-500/20",
  ];

  return colorPairs[roleId % colorPairs.length];
};

// Get role priority - lower number means higher priority
const getRolePriority = (roleId: number): number => {
  // Define role priorities (1 = highest, 10 = lowest)
  const rolePriorities: Record<number, number> = {
    1: 1, // SuperAdmin (highest priority)
    2: 2, // Admin
    3: 3, // Manager
    4: 4, // Editor
    5: 5, // Standard User
    // Add more role priorities as needed
  };

  return rolePriorities[roleId] || 10; // Default to lowest priority if not found
};

const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);

  const isNewUser = !id || id === "new";
  const title = isNewUser ? "Add User" : "Edit User";

  // Check if current user has admin privileges to manage roles
  const hasAdminPrivileges = (): boolean => {
    if (!currentUser || !currentUser.roles) return false;

    // Check if the user has any system admin role
    return currentUser.roles.some((role) => role.isSystem === true);
  };

  // Check for admin permission and show dialog if needed
  useEffect(() => {
    if (!hasAdminPrivileges()) {
      setPermissionDialogOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch all roles for assignment
  const { data: userRoles = [] } = useQuery({
    queryKey: ["userRoles"],
    queryFn: fetchUserRoles,
  });

  // Form state
  const [formData, setFormData] = useState<UserFormData>({
    name: "",
    email: "",
    avatarUrl: "",
    ageGroup: undefined,
    isActive: true,
  });
  const [roleIds, setRoleIds] = useState<number[]>([5]); // Default to Standard User (always required) + optionally one additional role
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [unauthorizedMessage, setUnauthorizedMessage] = useState<string>("");

  // Get the current user's highest priority role
  const getCurrentUserHighestRolePriority = (): number => {
    if (
      !currentUser ||
      !currentUser.roleIds ||
      currentUser.roleIds.length === 0
    ) {
      return 100; // Very low priority if no roles
    }

    // Get the lowest priority number (highest priority role)
    return Math.min(...currentUser.roleIds.map(getRolePriority));
  };

  // Check if current user can manage a specific role
  const canManageRole = (roleId: number): boolean => {
    // Admin users can only manage roles with lower priority (higher priority number) than their highest role
    const currentUserHighestPriority = getCurrentUserHighestRolePriority();
    const targetRolePriority = getRolePriority(roleId);

    return currentUserHighestPriority < targetRolePriority;
  };

  // If editing, fetch current user data
  useEffect(() => {
    if (!isNewUser) {
      const loadUser = async () => {
        try {
          const userData = await fetchUser(parseInt(id || "0", 10));
          if (userData) {
            setFormData({
              name: userData.name,
              email: userData.email,
              avatarUrl: userData.avatarUrl || "",
              ageGroup: userData.ageGroup as (typeof AGE_GROUPS)[number],
              isActive: userData.isActive,
            });
            // Ensure Standard User role (ID 5) is always included
            const userRoleIds = userData.roleIds || [5];
            if (!userRoleIds.includes(5)) {
              userRoleIds.push(5);
            }
            setRoleIds(userRoleIds);

            // Check if the current user can manage this user's roles
            if (!hasAdminPrivileges()) {
              setUnauthorizedMessage(
                "Only system administrators can manage user roles."
              );
            } else {
              // Get user's highest role priority
              const userHighestRolePriority = Math.min(
                ...userData.roleIds.map(getRolePriority)
              );
              const currentUserHighestPriority =
                getCurrentUserHighestRolePriority();

              if (currentUserHighestPriority >= userHighestRolePriority) {
                setUnauthorizedMessage(
                  "You cannot manage users with equal or higher role priority than yours."
                );
              }
            }
          } else {
            // User not found, redirect to users list
            toast({
              title: "User not found",
              description: "The requested user does not exist.",
              variant: "destructive",
            });
            navigate("/admin/users");
          }
        } catch (error) {
          console.error("Error loading user:", error);
          toast({
            title: "Error",
            description: "Failed to load user data.",
            variant: "destructive",
          });
        }
      };

      loadUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewUser, id, navigate, toast]);

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear the error for this field when the user types
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleAvatarSelect = (url: string) => {
    setFormData((prev) => ({ ...prev, avatarUrl: url }));
  };

  // Handle age group selection
  const handleAgeGroupChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      ageGroup:
        value === "none" ? undefined : (value as (typeof AGE_GROUPS)[number]),
    }));

    // Clear the error for this field when the user selects
    if (errors.ageGroup) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.ageGroup;
        return newErrors;
      });
    }
  };

  // Handle role toggle
  const handleRoleToggle = (roleId: number) => {
    // Role ID 5 (Standard User) should always be selected and cannot be toggled
    if (roleId === 5) {
      return;
    }

    // For security, check if user can manage this role
    if (!canManageRole(roleId)) {
      setUnauthorizedMessage("You don't have permission to assign this role.");
      return;
    }

    setUnauthorizedMessage("");
    setRoleIds((prev) => {
      if (prev.includes(roleId)) {
        // Remove the role if it's already selected
        return prev.filter((id) => id !== roleId);
      } else {
        // Only allow one additional role besides Standard User (ID 5)
        // Remove all other non-standard roles and add the new one
        return [5, roleId];
      }
    });
  };

  // Validate the form using zod schema
  const validateForm = (): boolean => {
    try {
      userSchema.parse(formData);

      // Additional validation for roles
      // Ensure Standard User role (ID 5) is always included
      if (!roleIds.includes(5)) {
        setErrors((prev) => ({
          ...prev,
          roles: "Standard User role is required and must always be assigned",
        }));
        return false;
      }

      // Ensure no more than 2 roles total (Standard User + 1 additional)
      if (roleIds.length > 2) {
        setErrors((prev) => ({
          ...prev,
          roles:
            "You can only assign one additional role besides the Standard User role",
        }));
        return false;
      }

      // Check for unauthorized role assignments
      const unauthorizedRoles = roleIds.filter(
        (roleId) => !canManageRole(roleId)
      );
      if (unauthorizedRoles.length > 0) {
        setErrors((prev) => ({
          ...prev,
          roles: "You don't have permission to assign some of these roles",
        }));
        return false;
      }

      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // Ensure Standard User role (ID 5) is always included before submission
      const finalRoleIds = roleIds.includes(5) ? roleIds : [...roleIds, 5];

      if (isNewUser) {
        // Create new user using auth function
        await createUserWithAuth({
          name: formData.name,
          email: formData.email,
          avatarUrl: formData.avatarUrl,
          ageGroup: formData.ageGroup,
          isActive: true, // Default to active for new users
          roleIds: finalRoleIds,
        });

        toast({
          title: "User created",
          description: `${formData.name} has been created successfully and an invitation email has been sent.`,
        });
      } else {
        // Update existing user
        await updateUser(parseInt(id!, 10), {
          name: formData.name,
          email: formData.email,
          avatarUrl: formData.avatarUrl,
          ageGroup: formData.ageGroup,
          isActive: formData.isActive, // Preserve original status
          roleIds: finalRoleIds,
        });
        toast({
          title: "User updated",
          description: `${formData.name} has been updated successfully.`,
        });
      }

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user", id] });

      // Redirect back to users management
      navigate("/admin/users");
    } catch (error) {
      console.error("Error saving user:", error);

      // Show error message
      let errorMessage = "An error occurred while saving the user";
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminLayout title={title}>
      <div className="space-y-6 max-w-2xl mx-auto">
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Button>

        <Card className="shadow-sm mb-6">
          <form onSubmit={handleSubmit}>
            <CardHeader>
              <CardTitle>
                {isNewUser ? "Create New User" : "Edit User"}
              </CardTitle>
              <CardDescription>
                {isNewUser
                  ? "Fill out the form below to create a new user account."
                  : "Update the user information below."}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6 pb-4">
              <div className="space-y-2">
                <Label
                  htmlFor="name"
                  className={errors.name ? "text-destructive" : ""}
                >
                  Full Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="Enter user's full name"
                  value={formData.name}
                  onChange={handleChange}
                  className={errors.name ? "border-destructive" : ""}
                />
                {errors.name && (
                  <p className="text-destructive text-sm">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className={errors.email ? "text-destructive" : ""}
                >
                  Email Address
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter user's email address"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? "border-destructive" : ""}
                  disabled={!isNewUser} // Make email read-only when editing
                />
                {errors.email && (
                  <p className="text-destructive text-sm">{errors.email}</p>
                )}
                {isNewUser && (
                  <p className="text-xs text-muted-foreground mt-1">
                    An invitation email will be sent to the user. They will need
                    to click the link in the email to set their password.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="avatarUrl">User Avatar</Label>
                <MediaPicker
                  onSelect={handleAvatarSelect}
                  currentValue={formData.avatarUrl}
                  allowedTypes={["image/jpeg", "image/jpg", "image/png"]}
                  title="Select User Avatar"
                  description="Choose an avatar image for this user from your media library or upload a new one"
                  buttonText="Select Avatar Image"
                  showPreview={true}
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="ageGroup"
                  className={errors.ageGroup ? "text-destructive" : ""}
                >
                  Age Group{" "}
                  <span className="text-muted-foreground">(Optional)</span>
                </Label>
                <Select
                  value={formData.ageGroup || "none"}
                  onValueChange={handleAgeGroupChange}
                >
                  <SelectTrigger
                    className={errors.ageGroup ? "border-destructive" : ""}
                  >
                    <SelectValue placeholder="Select age group (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-muted-foreground">
                        Not specified
                      </span>
                    </SelectItem>
                    {AGE_GROUPS.map((ageGroup) => (
                      <SelectItem key={ageGroup} value={ageGroup}>
                        {ageGroup}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.ageGroup && (
                  <p className="text-destructive text-sm">{errors.ageGroup}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Age group information helps with demographic analysis and
                  content personalization.
                </p>
              </div>

              <div className="space-y-2">
                <Label className={errors.roles ? "text-destructive" : ""}>
                  User Roles
                </Label>

                <p className="text-sm text-muted-foreground">
                  Every user has the Standard User role by default. You can
                  assign one additional role for elevated permissions.
                </p>

                {/* Display warning message if needed */}
                {unauthorizedMessage && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 mb-4">
                    <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-400">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm">{unauthorizedMessage}</span>
                    </div>
                  </div>
                )}

                <div className="space-y-3 border rounded-md p-3">
                  {/* Standard User Role - Always shown first and readonly */}
                  {userRoles
                    .filter((role) => role.id === 5)
                    .map((role) => (
                      <div
                        key={role.id}
                        className="flex items-center space-x-3 rounded-md border p-2 bg-muted/30 border-muted"
                      >
                        <Checkbox
                          id={`role-${role.id}`}
                          checked={true}
                          disabled={true}
                        />
                        <Label
                          htmlFor={`role-${role.id}`}
                          className="cursor-not-allowed flex-1"
                        >
                          <div className="flex items-center justify-between">
                            <span>{role.name}</span>
                            <Badge variant="secondary" className="text-xs">
                              Required
                            </Badge>
                          </div>
                        </Label>
                      </div>
                    ))}

                  {/* Additional Role Selection */}
                  <div className="border-t pt-3 mt-3">
                    <Label className="text-sm font-medium text-muted-foreground mb-2 block">
                      Additional Role (Optional - Select one)
                    </Label>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {userRoles
                        .filter((role) => role.id !== 5)
                        .map((role) => {
                          const isDisabled = !canManageRole(role.id);
                          const isSelected = roleIds.includes(role.id);

                          return (
                            <div
                              key={role.id}
                              className={cn(
                                "flex items-center space-x-3 rounded-md border p-2 transition-colors",
                                isSelected && "bg-primary/5 border-primary/20",
                                isDisabled && "opacity-50"
                              )}
                            >
                              <Checkbox
                                id={`role-${role.id}`}
                                checked={isSelected}
                                onCheckedChange={() =>
                                  handleRoleToggle(role.id)
                                }
                                disabled={isDisabled}
                              />
                              <Label
                                htmlFor={`role-${role.id}`}
                                className={cn(
                                  "flex-1",
                                  isDisabled
                                    ? "cursor-not-allowed"
                                    : "cursor-pointer"
                                )}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="font-medium">
                                      {role.name}
                                    </div>
                                    {role.description && (
                                      <div className="text-xs text-muted-foreground">
                                        {role.description}
                                      </div>
                                    )}
                                  </div>
                                  {isSelected && (
                                    <Badge className="text-xs">Selected</Badge>
                                  )}
                                </div>
                              </Label>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex justify-end border-t py-4">
              <Button type="submit" disabled={isSaving}>
                {isSaving
                  ? isNewUser
                    ? "Creating..."
                    : "Saving..."
                  : isNewUser
                  ? "Create User"
                  : "Save"}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>

      {/* Permission Dialog */}
      <Dialog
        open={permissionDialogOpen}
        onOpenChange={(open) => {
          setPermissionDialogOpen(open);
          if (!open) {
            navigate("/admin/users");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Permission Required
            </DialogTitle>
            <DialogDescription>
              You need administrator privileges to manage users. This action is
              restricted to system administrators only.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => {
                setPermissionDialogOpen(false);
                navigate("/admin/users");
              }}
            >
              Return to Users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default UserForm;
